import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  InputAdornment,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import ClientAutocomplete from "../clientAutocomplete";
import { purchaseOrderService } from "../../utils/services/purchaseOrderService";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { supplierService } from "../../utils/services/supplierService";
import buildQuery from "../../utils/query-builder";
import { clientConfigService } from "../../utils/services/clientConfigService";
import { padNumberWithZeros } from "../../utils/utility";
import CurrencyTextField from "../currency-field/CurrencyTextField";
import { useDarkMode } from "../context/darkModeContext";

const init_data = {
  client: null,
  no: "",
  date: null,
  expiration_date: null,
  amount: "",
  supplier: null,
};
const PurchaseOrderModal = ({
  open,
  handleClose,
  onAddData,
  selectedPurchaseOrder,
}) => {
  const { colors } = useDarkMode();

  const [formData, setFormData] = useState(init_data);
  const [formErrors, setFormErrors] = useState({});
  const client_id = localStorage.getItem("client_id");
  const [showCalendarFor, setShowCalendarFor] = useState(null);
  const calendarRef = useRef(null);

  useEffect(() => {
    if (client_id || formData.client?._id) {
      fetchOrderPrefix();
    }
  }, [client_id, formData.client]);

  let orderPrefix = "";

  const fetchOrderPrefix = async () => {
    try {
      const response = await clientConfigService.fetch(
        client_id || formData.client?._id,
        1,
        0,
        "master_purchase_order_prefix",
      );
      fetchPurchaseOrderCount();

      if (response && response.total > 0) {
        orderPrefix = response.data[0].value || "";
      }
    } catch (error) {
      console.error("Error fetching order prefix:", error);
    }
  };

  const fetchPurchaseOrderCount = async () => {
    try {
      const response = await purchaseOrderService.fetch(
        "$limit=0",
        client_id || formData.client?._id,
      );
      setFormData((prevOrder) => ({
        ...prevOrder,
        no: `${orderPrefix}${padNumberWithZeros(response.total + 1)}`,
      }));
    } catch (error) {
      console.error("Error fetching order count:", error);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        event.target.getAttribute("name") !== "date-field"
      ) {
        setShowCalendarFor(null);
      }
    }

    if (showCalendarFor) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendarFor]);

  useEffect(() => {
    if (selectedPurchaseOrder) {
      setFormData({
        client: selectedPurchaseOrder.client || null,
        no: selectedPurchaseOrder.no || "",
        date: selectedPurchaseOrder.date
          ? new Date(selectedPurchaseOrder.date)
          : null,
        expiration_date: selectedPurchaseOrder.expiration_date
          ? new Date(selectedPurchaseOrder.expiration_date)
          : null,
        amount: String(selectedPurchaseOrder.amount || ""),
        supplier: selectedPurchaseOrder.supplier,
      });
    } else {
      resetForm();
    }
  }, [selectedPurchaseOrder]);

  const resetForm = () => {
    setFormData(init_data);
    setFormErrors({});
    setShowCalendarFor(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: null }));
  };
  const validateForm = () => {
    const errors = {};

    if (!formData.client && !client_id) {
      errors.client = "Client is required";
      // toast.error("Client is required.", {
      //   autoClose: 2000,
      // });
    }
    if (!formData.no) {
      errors.no = "Master PO number is required";
    }
    if (!formData.amount && formData.amount !== 0) {
      errors.amount = "Master PO amount is required";
    } else if (isNaN(formData.amount) || Number(formData.amount) < 0) {
      errors.amount = "Master PO amount must be a number greater than 0";
    }
    if (!formData.date) {
      errors.date = "Master PO date is required";
    }
    if (!formData.supplier) {
      errors.supplier = "Supplier is required";
    }
    if (!formData.expiration_date) {
      errors.expiration_date = "Master PO expiration date is required";
    }
    const poDate = dayjs(formData.date).startOf("day");
    const expirationDate = dayjs(formData.expiration_date).startOf("day");
    if (
      formData.date &&
      formData.expiration_date &&
      expirationDate.isBefore(poDate)
    ) {
      errors.expiration_date =
        "Master PO Expiration Date must be greater than or equal to PO Date";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await purchaseOrderService.create({
        client: client_id || formData.client?._id,
        no: formData.no,
        date: formData.date,
        expiration_date: formData.expiration_date,
        amount: formData.amount,
        supplier: formData.supplier?._id,
      });
      toast.success("Master Purchase order created successfully!");
      onAddData && onAddData();
      handleClose();
      resetForm();
    } catch (e) {
      toast.error("Failed to create Master purchase order.");
      console.error(e);
    }
  };

  const handleEditData = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await purchaseOrderService.edit(selectedPurchaseOrder._id, {
        client: formData.client?._id,
        no: formData.no,
        date: formData.date,
        expiration_date: formData.expiration_date,
        amount: formData.amount,
        supplier: formData.supplier?._id,
      });
      toast.success("Master Purchase order updated successfully!");
      onAddData && onAddData();
      handleClose();
      resetForm();
    } catch (e) {
      toast.error("Failed to update Master purchase order.");
      console.error(e);
    }
  };

  const onChangeDate = (date) => {
    if (!showCalendarFor) return;
    if (showCalendarFor === "date") {
      setFormData((prev) => ({ ...prev, date: date, expiration_date: null }));
    } else {
      setFormData((prev) => ({ ...prev, expiration_date: date }));
    }
    setFormErrors((prev) => ({ ...prev, [showCalendarFor]: null }));
    setShowCalendarFor(null);
  };
  console.log(formData);

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
        resetForm();
      }}
      maxWidth="md"
      aria-labelledby="form-dialog-title"
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DialogTitle
          id="form-dialog-title"
          style={{
            fontFamily: "Poppins",
            fontSize: 24,
            fontWeight: 500,
            color: colors.textPrimary,
          }}
        >
          {selectedPurchaseOrder ? "Edit" : "Create"} Master Purchase Order
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 10 }} />
      <DialogContent>
        <Grid container spacing={2}>
          {!client_id && (
            <Grid item xs={12} sm={12} md={12}>
              <ClientAutocomplete
                backgroundColor={colors.bgPaper}
                label="Choose Client"
                selectedClient={formData.client}
                onClientChange={(client) => {
                  setFormData((prev) => ({
                    ...prev,
                    client: client,
                    supplier: null,
                  }));
                }}
                errorMessage={formErrors.client}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={6}>
            <AutocompleteWithPagination
              disabled={client_id || formData.client?._id ? false : true}
              selectedClient={formData.client || { _id: client_id }}
              backgroundColor={colors.bgPaper}
              customLabel={"Supplier"}
              selectedItem={formData.supplier}
              onDataChange={(supplier) => {
                setFormErrors((prev) => ({ ...prev, supplier: null }));
                if (supplier) {
                  setFormData((prev) => ({ ...prev, supplier: supplier }));
                } else {
                  setFormData((prev) => ({ ...prev, supplier: null }));
                }
              }}
              width={"100%"}
              optionKey={"name"}
              apiEndPoint={(limit, skip, search, client) => {
                const query = buildQuery({ limit: limit, skip: skip });
                return supplierService.fetch(search, client, query);
              }}
              errorMessage={formErrors.supplier}
            ></AutocompleteWithPagination>
            {/* <SupplierAutocomplete
              disabled={client_id || formData.client?._id ? false : true}
              backgroundColor={'#FFF'}
              selectedSupplier={formData.supplier}
              onSupplierChange={(supplier) => {
                setFormData((prev) => ({ ...prev, supplier: supplier }))
              }}
              errorMessage={formErrors?.supplier}
              clientId={client_id || formData.client?._id}
            /> */}
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              sx={{ backgroundColor: colors.bgPaper }}
              size="small"
              name="no"
              label="Master P.O. Number"
              value={formData.no}
              onChange={(e) => handleInputChange("no", e.target.value)}
              fullWidth
              margin="dense"
              error={!!formErrors.no}
              helperText={formErrors.no}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CurrencyTextField
              sx={{ backgroundColor: colors.bgPaper }}
              size="small"
              name="amount"
              label="Master P.O. Amount"
              value={formData.amount}
              onChange={(value) => handleInputChange("amount", value)}
              fullWidth
              margin="dense"
              error={!!formErrors.amount}
              helperText={formErrors.amount}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="date-field"
              margin="dense"
              label="Master P.O. Date"
              size="small"
              value={
                formData.date ? dayjs(formData.date).format("YYYY-MM-DD") : ""
              }
              onFocus={() => setShowCalendarFor("date")}
              inputProps={{ readOnly: true }}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: !!formData.date }}
              InputProps={
                formData.date
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            onClick={() => setShowCalendarFor("date")}
                          >
                            <CalendarIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  : null
              }
              error={!!formErrors.date}
              helperText={formErrors.date}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="date-field"
              margin="dense"
              label="Master P.O. Expiration Date"
              size="small"
              value={
                formData.expiration_date
                  ? dayjs(formData.expiration_date).format("YYYY-MM-DD")
                  : ""
              }
              onFocus={() => setShowCalendarFor("expiration_date")}
              inputProps={{ readOnly: true }}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: !!formData.expiration_date }}
              InputProps={
                formData.expiration_date
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            onClick={() =>
                              setShowCalendarFor("expiration_date")
                            }
                          >
                            <CalendarIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  : null
              }
              error={!!formErrors.expiration_date}
              helperText={formErrors.expiration_date}
            />
          </Grid>
        </Grid>

        {showCalendarFor && (
          <Box
            ref={calendarRef}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 3000,
              bgcolor: colors.bgPaper,
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
              borderRadius: 1,
              p: 0.5,
            }}
          >
            <Calendar
              value={formData[showCalendarFor]}
              onChange={(date) => onChangeDate(date)}
              minDate={showCalendarFor === "date" ? new Date() : formData.date}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => {
            handleClose();
            resetForm();
          }}
          color="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={(e) =>
            selectedPurchaseOrder ? handleEditData(e) : handleSave(e)
          }
          color="primary"
          variant="contained"
        >
          {selectedPurchaseOrder ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseOrderModal;
